import React, { createRef, useEffect, useState, useCallback } from 'react';
import { Icon, Input, Search, SearchProps } from 'semantic-ui-react';

export interface Props {
  placeHolder: string;
  onSubmit: (value: string) => void;
  loading: boolean;
  searchString: string;
  submitAlways?: boolean;
  size?: 'mini' | 'small' | 'large' | 'big' | 'huge' | 'massive';
  fluid?: boolean;
  fluidInput?: boolean;
  focusOnRender?: boolean;
  delay?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  shouldSubmitOnBlur?: boolean;
}

export const SearchPanel = (props: Props) => {
  const [searchString, setSearchString] = useState('');
  const [delayTimeout, setDelayTimeout] = useState<number>();
  const [placeholderString, setPlaceholderString] = useState(props.placeHolder);

  const searchRef = createRef<Input>();

  useEffect(() => {
    setSearchString(props.searchString);
  }, [props.searchString]);

  useEffect(() => {
    setPlaceholderString(props.placeHolder);
  }, [props.placeHolder]);

  useEffect(() => {
    if (props.focusOnRender) searchRef.current?.focus();
  }, [props.focusOnRender, searchRef]);

  const submit = useCallback((value: string) => props.onSubmit(value), [props]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!props.submitAlways && e.key === 'Enter') {
        submit(searchString);
      }
    },
    [props.submitAlways, searchString, submit]
  );

  const onSearchChange = (
    event: React.MouseEvent<HTMLElement>,
    data: SearchProps
  ) => {
    const value = data.value ?? '';
    setSearchString(value);

    if (props.submitAlways) {
      if (delayTimeout) {
        window.clearTimeout(delayTimeout);
      }

      const timeout = window.setTimeout(() => {
        submit(value);
      }, props.delay ?? 400);

      setDelayTimeout(timeout);
    }
  };

  const handleDeleteClick = useCallback(() => {
    setSearchString('');
    submit('');
    setPlaceholderString(props.placeHolder);
  }, [props.placeHolder, submit]);

  const { onFocus, onBlur, onSubmit, placeHolder, shouldSubmitOnBlur } = props;

  const handleOnFocus = useCallback(() => {
    onFocus?.();
    setPlaceholderString('');
  }, [onFocus]);

  const handleOnBlur = useCallback(() => {
    onBlur?.();
    shouldSubmitOnBlur && onSubmit(searchString);
    setPlaceholderString(placeHolder);
  }, [onBlur, onSubmit, placeHolder, shouldSubmitOnBlur, searchString]);

  return (
    <Search
      value={searchString}
      onSearchChange={onSearchChange}
      showNoResults={false}
      size={props.size}
      loading={props.loading}
      fluid={props.fluid}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
      placeholder={placeholderString}
      input={{
        fluid: props.fluidInput,
        type: 'text',
        ref: searchRef,
        icon: (
          <Icon
            className={`pos ${
              searchString && searchString !== '' ? 'close' : 'search'
            }`}
            onClick={handleDeleteClick}
            link
          />
        ),
      }}
      onKeyDown={handleKeyDown}
      data-testid="input-search-panel"
    />
  );
};
